import * as React from "react";
import {
  CheckCircle, Clock, XCircle, ProhibitInset, Crown, Play, CheckSquare,
  Phone, DotsThree, Timer,
} from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import type { Appointment, AppointmentStatus, DailyTimelineLabels } from "./types";
import { cn } from "@/lib/utils";
import { fmtTime, diffMinutes, type CardDensity } from "./utils";

const STATUS_ICON: Record<AppointmentStatus, Icon> = {
  confirmed: CheckCircle,
  pending: Clock,
  cancelled: XCircle,
  no_show: ProhibitInset,
  in_progress: Play,
  completed: CheckSquare,
};

const STATUS_BORDER: Record<AppointmentStatus, string> = {
  confirmed: "border-l-biz-success",
  pending: "border-l-biz-warning",
  cancelled: "border-l-biz-danger",
  no_show: "border-l-biz-border-strong",
  in_progress: "border-l-biz-info",
  completed: "border-l-biz-primary",
};

const STATUS_TEXT_COLOR: Record<AppointmentStatus, string> = {
  confirmed: "text-biz-success",
  pending: "text-biz-warning",
  cancelled: "text-biz-danger",
  no_show: "text-biz-foreground-subtle",
  in_progress: "text-biz-info",
  completed: "text-biz-primary",
};

interface TimelineCardProps {
  appointment: Appointment;
  density: CardDensity;
  now: Date;
  labels: DailyTimelineLabels;
  isLargeMode?: boolean;
  onTap?: (a: Appointment) => void;
  onCall?: (phone: string) => void;
  onMenu?: (a: Appointment) => void;
}

export const TimelineCard = React.forwardRef<HTMLButtonElement, TimelineCardProps>(
  function TimelineCard(
    { appointment, density, now, labels, isLargeMode, onTap, onCall, onMenu },
    ref,
  ) {
    const StatusIcon = STATUS_ICON[appointment.status];
    const isCancelled = appointment.status === "cancelled";
    const start = fmtTime(appointment.startTime);
    const end = fmtTime(appointment.endTime);

    const aria = `${labels.statusLabels[appointment.status]}: ${appointment.petName}, ${appointment.serviceName}, ${start}–${end}${appointment.isVip ? `, ${labels.vipBadge}` : ""}`;

    const baseText = isLargeMode ? "text-base" : "text-sm";
    const baseTitle = isLargeMode ? "text-lg" : "text-base";

    if (density === "past") {
      return (
        <button
          ref={ref}
          type="button"
          onClick={() => onTap?.(appointment)}
          aria-label={aria}
          className={cn(
            "w-full text-left flex items-center gap-2 px-3 py-2 rounded-biz-card border-l-[3px] bg-biz-muted/60 biz-shadow-sm",
            "biz-focus-ring biz-motion-safe transition-colors hover:bg-biz-muted",
            "min-h-[72px]",
            STATUS_BORDER[appointment.status],
          )}
        >
          <StatusIcon size={16} weight="regular" className={cn("shrink-0", STATUS_TEXT_COLOR[appointment.status])} />
          <span className={cn("font-mono-num text-xs text-biz-foreground-muted shrink-0")}>
            {start}–{end}
          </span>
          <span className={cn(
            "truncate text-xs text-biz-foreground-muted flex-1 min-w-0",
            isCancelled && "line-through",
          )}>
            {appointment.petName} · {appointment.serviceName}
          </span>
          <span className="text-[10px] uppercase tracking-wider text-biz-foreground-subtle shrink-0">
            {labels.statusLabels[appointment.status]}
          </span>
        </button>
      );
    }

    const minutesUntil = diffMinutes(appointment.startTime, now);
    const minutesElapsed = diffMinutes(now, appointment.startTime);
    const isActive = density === "active";

    return (
      <button
        ref={ref}
        type="button"
        onClick={() => onTap?.(appointment)}
        aria-label={aria}
        className={cn(
          "w-full text-left rounded-biz-card border-l-4 bg-biz-surface-elevated biz-shadow-sm",
          "biz-focus-ring biz-motion-safe transition-all p-4",
          isActive
            ? "biz-shadow-md ring-2 ring-biz-primary/30 min-h-[180px]"
            : "min-h-[120px] hover:biz-shadow-md",
          STATUS_BORDER[appointment.status],
          appointment.status === "pending" && "border border-dashed border-biz-warning-border",
        )}
      >
        {/* Top row: status + countdown + VIP/icons */}
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className={cn(
              "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
              "bg-biz-muted",
              STATUS_TEXT_COLOR[appointment.status],
            )}>
              <StatusIcon size={12} weight="regular" />
              {labels.statusLabels[appointment.status]}
            </span>
            <span className="font-mono-num text-xs text-biz-foreground-muted">
              {start}–{end}
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {appointment.isVip && (
              <span
                className="inline-flex items-center gap-0.5 rounded-full bg-biz-accent-subtle px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                style={{ color: "hsl(var(--biz-accent))" }}
                aria-label={labels.vipBadge}
              >
                <Crown size={12} weight="regular" />
                {labels.vipBadge}
              </span>
            )}
            {isActive && (
              <span className={cn(
                "inline-flex items-center gap-1 font-mono-num text-xs font-semibold rounded-full px-2 py-0.5",
                minutesElapsed > 0
                  ? "bg-biz-warning-subtle text-biz-warning"
                  : "bg-biz-info-subtle text-biz-info",
              )}>
                <Timer size={12} weight="regular" />
                {minutesElapsed > 0
                  ? labels.elapsed({ minutes: minutesElapsed })
                  : labels.countdown({ minutes: Math.max(0, minutesUntil) })}
              </span>
            )}
          </div>
        </div>

        {/* Pet + service */}
        <div className={cn("font-display font-semibold text-biz-foreground", baseTitle, isCancelled && "line-through opacity-70")}>
          {appointment.petName}
          <span className="text-biz-foreground-muted font-normal mx-1">·</span>
          <span>{appointment.serviceName}</span>
        </div>

        {/* Customer */}
        <div className={cn("text-biz-foreground-muted mt-1 truncate", baseText)}>
          {appointment.customerName}
        </div>

        {/* Notes preview (active only) */}
        {isActive && appointment.notes && (
          <div className="mt-2 text-xs text-biz-foreground-muted line-clamp-2 bg-biz-muted/50 rounded-md px-2 py-1.5">
            <span className="font-semibold mr-1 text-biz-foreground-subtle uppercase tracking-wider text-[10px]">{labels.notesLabel}:</span>
            {appointment.notes}
          </div>
        )}

        {/* Bottom row: price + quick actions */}
        <div className="flex items-center justify-between gap-2 mt-3">
          <div className="font-mono-num text-sm font-semibold text-biz-foreground">
            {typeof appointment.price === "number"
              ? `${appointment.price.toLocaleString()} ${appointment.currency ?? ""}`.trim()
              : ""}
          </div>
          <div className="flex items-center gap-1">
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onCall?.(""); }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault(); e.stopPropagation(); onCall?.("");
                }
              }}
              aria-label={labels.callAction}
              className="inline-flex items-center justify-center h-14 w-14 rounded-biz-btn text-biz-foreground-muted hover:bg-biz-muted hover:text-biz-foreground biz-focus-ring biz-motion-safe transition-colors"
            >
              <Phone size={18} weight="regular" />
            </span>
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onMenu?.(appointment); }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault(); e.stopPropagation(); onMenu?.(appointment);
                }
              }}
              aria-label="Menu"
              className="inline-flex items-center justify-center h-14 w-14 rounded-biz-btn text-biz-foreground-muted hover:bg-biz-muted hover:text-biz-foreground biz-focus-ring biz-motion-safe transition-colors"
            >
              <DotsThree size={20} weight="regular" />
            </span>
          </div>
        </div>
      </button>
    );
  },
);
